/** @jsx jsx */
/** @jsxFrag React.Fragment */
import React, { FC } from "react";
import { jsx } from "@emotion/core";
import { bootstrapAppData } from "utils/bootstrap";
import * as authClient from "utils/auth-client";
import { useAsync } from "utils/use-async";
import createCtx from "utils/createCtx";
import * as colors from "styles/colors";
import { UserDetailDto } from "../../../server/src/users/dto/user-detail.dto";
import { EmployeeDetailDto } from "../../../server/src/staff/dto/employee-detail.dto";
import { CustomerDetailDto } from "../../../server/src/customers/dto/customer-detail.dto";
import { FullPageSpinner } from "component/lib";

type AuthCtxValue = {
  login: Function;
  register: Function;
  logout: Function;
  user: EmployeeDetailDto & UserDetailDto & CustomerDetailDto;
};

const AuthContext = createCtx<AuthCtxValue>("AuthContext");

const [useAuthCtx, AuthCtxProvider] = AuthContext;

let appDataPromise = bootstrapAppData();

const AuthProvider: FC = (props) => {
  const {
    data,
    status,
    error,
    isLoading,
    isIdle,
    isError,
    isSuccess,
    run,
    setData,
  } = useAsync();

  React.useLayoutEffect(() => {
    run(appDataPromise);
  }, [run]);

  React.useLayoutEffect(() => {
    if (isError) {
      window.location.reload();
    }
  }, [isError]);

  const login = React.useCallback(
    (form) => authClient.login(form).then((user) => setData({ user })),
    [setData]
  );
  const register = React.useCallback(
    (form) => authClient.register(form).then((user) => setData({ user })),
    [setData]
  );
  const logout = React.useCallback(() => {
    authClient.logout();
    setData(null);
  }, [setData]);

  const user = data?.user;

  const value = React.useMemo(() => ({ user, login, logout, register }), [
    login,
    logout,
    register,
    user,
  ]);

  if (isLoading || isIdle) {
    return <FullPageSpinner />;
  }

  if (isError) {
    return (
      <div
        css={{
          color: colors.red600,
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <p>مشکلی وجود دارد. لطفن برنامه را رفرش کنید.</p>
        <pre>{error.message}</pre>
      </div>
    );
  }

  if (isSuccess) {
    return <AuthCtxProvider value={value} {...props} />;
  }

  throw new Error(`Unhandled status: ${status}`);
};

function useAuth() {
  const context = useAuthCtx();
  if (context === undefined) {
    throw new Error(`useAuth must be used within a AuthProvider`);
  }
  return context;
}

export { AuthProvider, useAuth };
